import React, { useState, useRef, useEffect } from "react";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import {
  Stepper,
  Step,
  StepLabel,
  Button,
  Typography,
  Box,
  Paper,
  useTheme,
  useMediaQuery,
  CircularProgress,
} from "@mui/material";
import PersonalDetailsStep from "./steps/PersonalDetailsStep";
import BankDetailsStep from "./steps/BankDetailsStep";
import EducationExperienceStep from "./steps/EducationExperienceStep";
import { useDispatch } from "react-redux";
import AuthViewController from "../../redux/actions/AuthViewController";
import { useNavigate, useParams } from "react-router-dom";
import logo from "../../assets/Thryiv_logo_tm_TB.png";
import dayjs from "dayjs";
import {
  BRAND,
  cardSx,
  stepperSx,
  horizontalStepperSx,
  backButtonSx,
  footerPrimaryButtonSx,
  footerActionsSx,
  copyrightFooterSx,
  pageWrapperSx,
  formWrapperSx,
  sectionHeadingSx,
  sectionSubheadingSx,
  validateDocumentFile,
  isExperienceEntryFilled,
  filterFilledExperience,
} from "./onboardingTheme";

const INDIAN_MOBILE_REGEX = /^[6-9]\d{9}$/;
const LETTERS_ONLY_REGEX = /^[A-Za-z\s]+$/;
const IFSC_REGEX = /^[A-Z]{4}0[A-Z0-9]{6}$/i;
const PAN_REGEX = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/i;
const ALPHANUMERIC_REGEX = /^[A-Za-z0-9]+$/;
const CURRENT_YEAR = dayjs().year();

const containsLetter = (value) => /[A-Za-z]/.test(value || "");
const isDigitsOnly = (value) => /^\d+$/.test(value || "");

const documentFileSchema = (label) =>
  Yup.mixed()
    .required(`${label} is required`)
    .test("fileValidation", function (value) {
      if (!value) {
        return this.createError({ message: `${label} is required` });
      }
      const result = validateDocumentFile(value);
      if (!result.valid) {
        return this.createError({ message: result.message });
      }
      return true;
    });

const steps = [
  {
    label: "Personal Details",
    shortLabel: "Personal",
    description: "Basic profile & contact info",
  },
  {
    label: "Bank & Tax Details",
    shortLabel: "Bank & Tax",
    description: "Payment & statutory information",
  },
  {
    label: "Education & Experience",
    shortLabel: "Education",
    description: "Qualifications & work history",
  },
];

const initialValues = {
  profile_image: null,
  date_of_birth: "",
  address: {
    residentialAddress: "",
    currentAddress: "",
  },
  mobile_number: "",
  alternative_mobile_number: "",
  aadhar_number: "",
  about: "",
  gender: "",
  nationality: "",
  bank_name: "",
  account_number: "",
  ifsc_code: "",
  pan_number: "",
  pf_number: "",
  esi_number: "",
  education: [
    {
      name: "",
      percentage: "",
      passingYear: "",
      doc: null,
    },
  ],
  experience: [],
};

const validationSchemas = [
  Yup.object({
    date_of_birth: Yup.date()
      .transform((value, originalValue) =>
        originalValue === "" || originalValue == null ? null : value
      )
      .required("Date of birth is required")
      .max(
        dayjs().subtract(18, "year").endOf("day").toDate(),
        "You must be at least 18 years old"
      )
      .min(
        dayjs().subtract(100, "year").startOf("day").toDate(),
        "Please enter a valid date of birth"
      )
      .typeError("Invalid date"),
    mobile_number: Yup.string()
      .required("Mobile number is required")
      .matches(
        INDIAN_MOBILE_REGEX,
        "Enter a valid 10-digit Indian mobile number (starts with 6-9)"
      ),
    alternative_mobile_number: Yup.string()
      .nullable()
      .transform((value) => (value === "" ? null : value))
      .matches(
        INDIAN_MOBILE_REGEX,
        "Enter a valid 10-digit Indian mobile number (starts with 6-9)",
        { excludeEmptyString: true }
      ),
    address: Yup.object({
      residentialAddress: Yup.string()
        .required("Permanent address is required")
        .min(5, "Permanent address must be at least 5 characters"),
      currentAddress: Yup.string()
        .required("Current address is required")
        .min(5, "Current address must be at least 5 characters"),
    }),
    profile_image: documentFileSchema("Profile photo"),
    aadhar_number: Yup.string()
      .required("Aadhar number is required")
      .matches(/^\d{12}$/, "Aadhar number must be exactly 12 digits"),
    gender: Yup.string()
      .required("Gender is required")
      .oneOf(["male", "female", "other", "prefer_not_to_say"], "Invalid gender"),
    nationality: Yup.string()
      .required("Nationality is required")
      .matches(LETTERS_ONLY_REGEX, "Nationality must contain letters only")
      .test(
        "contains-letters",
        "Nationality must not be numbers only",
        (value) => containsLetter(value)
      ),
    about: Yup.string()
      .nullable()
      .transform((value) => (value === "" ? null : value))
      .min(10, "Profile summary must be at least 10 characters")
      .test(
        "not-numbers-only",
        "Profile summary must contain words, not numbers only",
        (value) => !value || !isDigitsOnly(value.trim())
      )
      .test(
        "contains-letters",
        "Profile summary must contain at least some letters",
        (value) => !value || containsLetter(value)
      ),
  }),
  Yup.object({
    bank_name: Yup.string()
      .required("Bank name is required")
      .min(2, "Bank name must be at least 2 characters"),
    account_number: Yup.string()
      .required("Account number is required")
      .matches(/^\d+$/, "Account number must contain digits only")
      .min(9, "Account number must be 9-18 digits")
      .max(18, "Account number must be 9-18 digits"),
    ifsc_code: Yup.string()
      .required("IFSC code is required")
      .matches(IFSC_REGEX, "Enter a valid IFSC code (e.g. SBIN0001234)"),
    pan_number: Yup.string()
      .nullable()
      .transform((value) => (value === "" ? null : value))
      .matches(PAN_REGEX, "Enter a valid PAN number (e.g. ABCDE1234F)", {
        excludeEmptyString: true,
      }),
    pf_number: Yup.string()
      .nullable()
      .transform((value) => (value === "" ? null : value))
      .matches(ALPHANUMERIC_REGEX, "PF number must be alphanumeric", {
        excludeEmptyString: true,
      }),
    esi_number: Yup.string()
      .nullable()
      .transform((value) => (value === "" ? null : value))
      .matches(ALPHANUMERIC_REGEX, "ESI number must be alphanumeric", {
        excludeEmptyString: true,
      }),
  }),
  Yup.object({
    education: Yup.array()
      .of(
        Yup.object().shape({
          name: Yup.string()
            .required("College/Institute name is required")
            .min(2, "College/Institute name must be at least 2 characters")
            .test(
              "contains-letters",
              "College/Institute name must contain letters",
              (value) => containsLetter(value)
            ),
          percentage: Yup.string()
            .required("Percentage or CGPA is required")
            .test(
              "valid-score",
              "Enter a valid percentage (0-100) or CGPA (0-10)",
              (value) => {
                if (!value || !/^\d+(\.\d{1,2})?$/.test(value.trim())) {
                  return false;
                }
                const num = parseFloat(value);
                if (Number.isNaN(num)) return false;
                if (num <= 10) return num >= 0 && num <= 10;
                return num >= 0 && num <= 100;
              }
            ),
          passingYear: Yup.string()
            .required("Passing year is required")
            .matches(/^\d{4}$/, "Passing year must be a 4-digit year")
            .test(
              "valid-year",
              `Passing year must be between 1950 and ${CURRENT_YEAR}`,
              (value) => {
                if (!value) return false;
                const year = parseInt(value, 10);
                return year >= 1950 && year <= CURRENT_YEAR;
              }
            ),
          doc: documentFileSchema("Education document"),
        })
      )
      .min(1, "At least one education entry is required"),
    experience: Yup.array().of(
      Yup.object().shape({
        company: Yup.string().test(
          "company-validation",
          "Company name is required",
          function (value) {
            if (!isExperienceEntryFilled(this.parent)) return true;
            if (!value || value.trim().length < 2) {
              return this.createError({
                message: "Company name must be at least 2 characters",
              });
            }
            if (!containsLetter(value)) {
              return this.createError({
                message: "Company name must contain letters",
              });
            }
            return true;
          }
        ),
        designation: Yup.string().test(
          "designation-validation",
          "Designation is required",
          function (value) {
            if (!isExperienceEntryFilled(this.parent)) return true;
            if (!value || value.trim().length < 2) {
              return this.createError({
                message: "Designation must be at least 2 characters",
              });
            }
            if (!containsLetter(value)) {
              return this.createError({
                message: "Designation must contain letters",
              });
            }
            return true;
          }
        ),
        joining: Yup.string().test(
          "joining-validation",
          "Joining date is required",
          function (value) {
            if (!isExperienceEntryFilled(this.parent)) return true;
            if (!value) {
              return this.createError({ message: "Joining date is required" });
            }
            return true;
          }
        ),
        end: Yup.string().test(
          "end-validation",
          "End date is required",
          function (value) {
            if (!isExperienceEntryFilled(this.parent)) return true;
            if (!value) {
              return this.createError({ message: "End date is required" });
            }
            const { joining } = this.parent;
            if (joining && new Date(value) < new Date(joining)) {
              return this.createError({
                message: "End date must be on or after joining date",
              });
            }
            return true;
          }
        ),
        doc: Yup.mixed()
          .nullable()
          .test(
            "experience-doc",
            "Experience document is required",
            function (value) {
              if (!isExperienceEntryFilled(this.parent)) return true;
              if (!value) {
                return this.createError({
                  message: "Experience document is required",
                });
              }
              const result = validateDocumentFile(value);
              if (!result.valid) {
                return this.createError({ message: result.message });
              }
              return true;
            }
          ),
      })
    ),
  }),
];

const EmployeeSelfOnboardingForm = () => {
  const { submitOnboardingForm } = AuthViewController();
  const { uid, token } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const theme = useTheme();
  const isDesktopLayout = useMediaQuery(theme.breakpoints.up("md"));
  const [activeStep, setActiveStep] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const contentRef = useRef(null);

  const scrollToFormTop = () => {
    if (isDesktopLayout && contentRef.current) {
      contentRef.current.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  useEffect(() => {
    if (!isDesktopLayout) {
      document.body.style.overflow = "";
      return undefined;
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isDesktopLayout]);

  const handleNext = () => setActiveStep((prev) => prev + 1);
  const handleBack = () => setActiveStep((prev) => prev - 1);

  const appendOptionalField = (formData, key, value) => {
    if (value != null && value !== "") {
      formData.append(key, value);
    }
  };

  const handleFinalSubmit = async (values) => {
    const formData = new FormData();

    formData.append("profile_image", values.profile_image);
    formData.append("date_of_birth", values.date_of_birth);
    formData.append("aadhar_number", values.aadhar_number);
    appendOptionalField(formData, "about", values.about);
    formData.append("bank_name", values.bank_name);
    formData.append("account_number", values.account_number);
    formData.append("ifsc_code", values.ifsc_code);
    appendOptionalField(formData, "pan_number", values.pan_number);
    appendOptionalField(formData, "pf_number", values.pf_number);
    appendOptionalField(formData, "esi_number", values.esi_number);
    formData.append("gender", values.gender);
    formData.append("nationality", values.nationality);
    formData.append("mobile_number", values.mobile_number);
    appendOptionalField(
      formData,
      "alternative_mobile_number",
      values.alternative_mobile_number
    );

    formData.append("residentialAddress", values.address.residentialAddress);
    formData.append("currentAddress", values.address.currentAddress);

    const educationData = values.education.map(({ doc, ...rest }) => rest);
    formData.append("education", JSON.stringify(educationData));
    values.education.forEach((edu, index) => {
      if (edu.doc) {
        formData.append(`education_docs[${index}]`, edu.doc);
      }
    });

    const filledExperience = filterFilledExperience(values.experience);
    const experienceData = filledExperience.map(({ doc, ...rest }) => rest);
    formData.append("experience", JSON.stringify(experienceData));
    filledExperience.forEach((exp, index) => {
      if (exp.doc) {
        formData.append(`experience_docs[${index}]`, exp.doc);
      }
    });

    setIsSubmitting(true);
    try {
      await dispatch(submitOnboardingForm(uid, token, formData, navigate));
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderStepContent = (stepProps) => {
    switch (activeStep) {
      case 0:
        return <PersonalDetailsStep {...stepProps} />;
      case 1:
        return <BankDetailsStep {...stepProps} />;
      case 2:
        return <EducationExperienceStep {...stepProps} />;
      default:
        return null;
    }
  };

  return (
    <Box sx={pageWrapperSx}>
      <Box
        sx={{
          flexShrink: 0,
          bgcolor: "#fff",
          borderBottom: `1px solid ${BRAND.border}`,
          py: { xs: 1, sm: 1.25, md: 1.5 },
          px: { xs: 1.5, sm: 2, md: 3 },
          maxWidth: "100%",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            justifyContent: "space-between",
            alignItems: { xs: "stretch", sm: "center" },
            gap: { xs: 0.75, sm: 0 },
            maxWidth: 1200,
            mx: "auto",
            width: "100%",
          }}
        >
          <Box sx={{ textAlign: "left", flex: 1, minWidth: 0 }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                color: BRAND.navy,
                fontSize: { xs: "1.1rem", md: "1.25rem" },
                lineHeight: 1.3,
              }}
            >
              Employee Self-Onboarding
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: BRAND.muted,
                mt: 0.25,
                display: "block",
                fontSize: { xs: "0.6875rem", sm: "0.75rem" },
                lineHeight: 1.4,
                maxWidth: "100%",
              }}
            >
              Complete your profile in a few simple steps to get started with
              your organization.
            </Typography>
          </Box>
          <Box
            sx={{
              flexShrink: 0,
              alignSelf: { xs: "center", sm: "center" },
              mt: { xs: 0.25, sm: 0 },
            }}
          >
            <img
              src={logo}
              alt="Thryiv"
              style={{
                height: 40,
                maxWidth: "100%",
                objectFit: "contain",
                display: "block",
              }}
            />
          </Box>
        </Box>
      </Box>

      <Formik
        initialValues={initialValues}
        validationSchema={validationSchemas[activeStep]}
        onSubmit={(values) => handleFinalSubmit(values)}
      >
        {(formikProps) => {
          const { values, validateForm, setSubmitting, setTouched } =
            formikProps;

          const handleStepSubmit = async () => {
            if (isSubmitting) return;

            const errors = await validateForm();

            if (Object.keys(errors).length > 0) {
              const buildTouched = (errObj) => {
                if (typeof errObj !== "object" || errObj === null) return true;
                return Object.keys(errObj).reduce((acc, key) => {
                  acc[key] = buildTouched(errObj[key]);
                  return acc;
                }, {});
              };
              setTouched(buildTouched(errors), true);
              setSubmitting(false);
              scrollToFormTop();
              return;
            }

            if (activeStep === steps.length - 1) {
              setSubmitting(true);
              try {
                await handleFinalSubmit(values);
              } catch {
                // Error toast is handled in submitOnboardingForm
              } finally {
                setSubmitting(false);
              }
            } else {
              setTouched({});
              handleNext();
              scrollToFormTop();
              setSubmitting(false);
            }
          };

          return (
            <Box component={Form} sx={formWrapperSx}>
              <Box
                sx={{
                  flex: { xs: "0 1 auto", md: 1 },
                  minHeight: { xs: "auto", md: 0 },
                  maxWidth: 1200,
                  width: "100%",
                  mx: "auto",
                  px: { xs: 1, sm: 2, md: 2.5 },
                  py: { xs: 1, sm: 1.25, md: 1.5 },
                  display: "flex",
                  flexDirection: { xs: "column", md: "row" },
                  gap: { xs: 1, sm: 1.25, md: 2 },
                  overflow: { xs: "visible", md: "hidden" },
                  boxSizing: "border-box",
                }}
              >
                <Paper
                  elevation={0}
                  sx={{
                    ...cardSx,
                    flexShrink: 0,
                    p: { xs: 1, sm: 1.25, md: 2 },
                    display: { xs: "block", md: "flex" },
                    flexDirection: "column",
                    width: { xs: "100%", md: 260 },
                    minHeight: 0,
                    overflow: { xs: "visible", md: "hidden" },
                    maxWidth: "100%",
                    boxSizing: "border-box",
                  }}
                >
                  <Typography
                    variant="overline"
                    sx={{
                      color: BRAND.muted,
                      fontWeight: 600,
                      letterSpacing: "0.08em",
                      display: { xs: "none", md: "block" },
                      mb: 1.5,
                      fontSize: "0.65rem",
                    }}
                  >
                    Progress
                  </Typography>

                  <Stepper
                    activeStep={activeStep}
                    orientation="vertical"
                    sx={{
                      ...stepperSx,
                      display: { xs: "none", md: "flex" },
                      "& .MuiStepConnector-line": { minHeight: 20 },
                    }}
                  >
                    {steps.map((step) => (
                      <Step key={step.label}>
                        <StepLabel>{step.label}</StepLabel>
                      </Step>
                    ))}
                  </Stepper>

                  <Stepper
                    activeStep={activeStep}
                    alternativeLabel
                    sx={horizontalStepperSx}
                  >
                    {steps.map((step) => (
                      <Step key={step.label}>
                        <StepLabel>{step.shortLabel}</StepLabel>
                      </Step>
                    ))}
                  </Stepper>
                </Paper>

                <Paper
                  elevation={0}
                  sx={{
                    ...cardSx,
                    flex: { xs: "0 1 auto", md: 1 },
                    minHeight: { xs: "auto", md: 0 },
                    display: "flex",
                    flexDirection: "column",
                    overflow: { xs: "visible", md: "hidden" },
                    maxWidth: "100%",
                    width: "100%",
                    boxSizing: "border-box",
                  }}
                >
                  <Box
                    ref={contentRef}
                    sx={{
                      flex: { xs: "0 1 auto", md: 1 },
                      minHeight: { xs: "auto", md: 0 },
                      overflowY: { xs: "visible", md: "auto" },
                      overflowX: "hidden",
                      WebkitOverflowScrolling: "touch",
                      p: { xs: 1.5, sm: 2, md: 2.5 },
                      maxWidth: "100%",
                      boxSizing: "border-box",
                    }}
                  >
                    <Typography sx={sectionHeadingSx}>
                      {steps[activeStep].label}
                    </Typography>
                    <Typography sx={sectionSubheadingSx}>
                      {steps[activeStep].description}
                    </Typography>

                    {renderStepContent(formikProps)}
                  </Box>

                  <Box sx={footerActionsSx}>
                    <Button
                      variant="outlined"
                      onClick={handleBack}
                      disabled={activeStep === 0 || isSubmitting}
                      sx={backButtonSx}
                      size="small"
                    >
                      Back
                    </Button>
                    <Button
                      variant="contained"
                      disableElevation
                      onClick={handleStepSubmit}
                      type="button"
                      size="small"
                      disabled={isSubmitting}
                      sx={footerPrimaryButtonSx}
                      startIcon={
                        isSubmitting ? (
                          <CircularProgress size={16} color="inherit" />
                        ) : null
                      }
                    >
                      {isSubmitting
                        ? "Submitting..."
                        : activeStep === steps.length - 1
                          ? "Submit"
                          : "Next"}
                    </Button>
                  </Box>
                </Paper>
              </Box>

              <Typography
                variant="caption"
                align="center"
                sx={copyrightFooterSx}
              >
                Copyright © 2026 — Swatsan Tech Pvt. Ltd.
              </Typography>
            </Box>
          );
        }}
      </Formik>
    </Box>
  );
};

export default EmployeeSelfOnboardingForm;
